/**
 * Per-tab state helpers for the eligibility worklist.
 */

import type { FilterValues } from "../../data-model/filterValues";
import type { EligibilityTableRow } from "../../data-model/eligibilityRows";

export const ELIGIBILITY_API_PAGE_SIZE = 50;
export const ELIGIBILITY_DISPLAY_PAGE_SIZE = 10;

/** Search, filters, sort, and page retained independently for one tab. */
export interface EligibilityTabViewState {
  searchText: string;
  appliedFilters: FilterValues | null;
  page: number;
  sortBy?: string;
  sortDir: "asc" | "desc";
}

/** Empty controls used when a worklist tab has not been visited. */
export const DEFAULT_TAB_VIEW_STATE: EligibilityTabViewState = {
  searchText: "",
  appliedFilters: null,
  page: 0,
  sortBy: "eligibility_tier",
  sortDir: "asc",
};

/**
 * Sort transformed worklist rows by the numeric tier in their display label.
 * Missing or unrecognized tiers stay at the end in either direction.
 *
 * @param rows - Transformed worklist rows
 * @param direction - Requested tier direction
 * @returns A sorted copy of the rows
 */
export function sortEligibilityRowsByTier(
  rows: EligibilityTableRow[],
  direction: "asc" | "desc" = "asc",
): EligibilityTableRow[] {
  const tierRank = (row: EligibilityTableRow): number | null => {
    const match = /tier[\s_-]*(\d+)/i.exec(row.eligibilityTier.text);
    return match ? Number(match[1]) : null;
  };

  return rows
    .map((row, index) => ({ row, index, rank: tierRank(row) }))
    .sort((left, right) => {
      if (left.rank === null && right.rank === null) {
        return left.index - right.index;
      }
      if (left.rank === null) return 1;
      if (right.rank === null) return -1;
      const rankDifference = left.rank - right.rank;
      if (rankDifference === 0) return left.index - right.index;
      return direction === "asc" ? rankDifference : -rankDifference;
    })
    .map(({ row }) => row);
}

/**
 * Return the first selected value for a server-side single-value filter.
 *
 * @param filters - Applied table filter values
 * @param key - Backend filter field
 * @returns First selected value, or undefined when the filter is empty
 */
export function firstFilterValue(
  filters: FilterValues | null,
  key: string,
): string | undefined {
  return filters?.values?.[key]?.values?.[0] || undefined;
}

/**
 * Convert a date-filter value into `YYYY-MM-DD` for eligibility query bounds.
 *
 * @param value - Date-range picker value (`MM/DD/YYYY` or ISO)
 * @returns ISO calendar date, or undefined when the value cannot be parsed
 */
export function toEligibilityIsoDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const slashMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (slashMatch) {
    return `${slashMatch[3]}-${slashMatch[1]}-${slashMatch[2]}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : parsed.toISOString().slice(0, 10);
}

/**
 * Return one bound of a date-range filter for screened-rows.
 *
 * The API compares visit timestamps as strings. The start bound stays a calendar
 * date (`YYYY-MM-DD`); the end bound extends through the last second of that day
 * so visits on the selected end date are included.
 *
 * @param filters - Applied table filter values
 * @param key - Backend filter field
 * @param index - `0` for the start date, `1` for the end date
 * @returns ISO timestamp bound, or undefined when that bound is empty
 */
export function dateFilterIsoValue(
  filters: FilterValues | null,
  key: string,
  index: number,
): string | undefined {
  const calendarDate = toEligibilityIsoDate(
    filters?.values?.[key]?.values?.[index],
  );
  if (!calendarDate) return undefined;
  if (index === 1) {
    return `${calendarDate}T23:59:59.999999`;
  }
  return calendarDate;
}

/**
 * Convert a zero-based display page into its containing API chunk.
 *
 * @param displayPage - Zero-based 10-row page selected in the UI
 * @returns Zero-based 50-row page requested from the API
 */
export function getEligibilityApiPage(displayPage: number): number {
  return Math.floor(
    (displayPage * ELIGIBILITY_DISPLAY_PAGE_SIZE) / ELIGIBILITY_API_PAGE_SIZE,
  );
}

/**
 * Return every API chunk needed to fill one display page.
 *
 * Most pages need one chunk; a page crossing a 50-row boundary needs two.
 *
 * @param displayPage - Zero-based 10-row page selected in the UI
 * @returns Ordered zero-based API page numbers
 */
export function getEligibilityApiPages(displayPage: number): number[] {
  const firstRow = displayPage * ELIGIBILITY_DISPLAY_PAGE_SIZE;
  const lastRow = firstRow + ELIGIBILITY_DISPLAY_PAGE_SIZE - 1;
  const firstPage = Math.floor(firstRow / ELIGIBILITY_API_PAGE_SIZE);
  const lastPage = Math.floor(lastRow / ELIGIBILITY_API_PAGE_SIZE);
  return firstPage === lastPage ? [firstPage] : [firstPage, lastPage];
}

/**
 * Select the visible 10 rows from the currently loaded API chunk or chunks.
 *
 * @param rows - Concatenated rows from the required ordered API pages
 * @param displayPage - Zero-based page selected in the UI
 * @returns Rows displayed for the selected UI page
 */
export function getEligibilityDisplayRows(
  rows: EligibilityTableRow[],
  displayPage: number,
): EligibilityTableRow[] {
  const start =
    (displayPage * ELIGIBILITY_DISPLAY_PAGE_SIZE) % ELIGIBILITY_API_PAGE_SIZE;
  return rows.slice(start, start + ELIGIBILITY_DISPLAY_PAGE_SIZE);
}
